import { setLocalStorageItem } from "@lib/localStorage";
import { t } from "i18next";

export const getValidityFields = ({ component, object, otherEmails = [] }) => {
  switch (component) {
    case "siteAdmin-partnerManager":
      return [
        [t("name"), "name", "name", object?.name || "", "name"],
        [
          t("email"),
          "email",
          "email-valid",
          object?.email || "",
          { validity: true },
        ],
        [
          t("email"),
          "email",
          "email-exists",
          object?.email || "",
          { otherEmails: otherEmails || [] },
        ],
      ];
  }
};

export const getRequiredFields = ({ component, object }) => {
  switch (component) {
    case "siteAdmin-partnerManager":
      return [
        [t("name"), "name", object?.name || "", "name"],
        [t("email"), "email", object?.email || "", "email"],
        [
          "partnerManagerGroup",
          "partnerManagerGroup",
          object?.productsPartnerManager ||
            object?.servicesPartnerManager ||
            object?.partnerBusinessManager,
          "partnerManagerGroup",
        ],
      ];
  }
};

const buildRequiredField = (name: string, id: string, value: any) => ({
  name: t(name),
  id,
  value,
});
const buildValidityField = (
  name: string,
  field: string,
  id: string,
  value: any,
  isValid: boolean
) => ({
  name: t(name),
  field,
  id,
  value,
  isValid,
});

export const logicForRequiredFields = ({
  requiredFields,
  getComponentsRequiredFields,
}) => {
  const requiredFieldsToAdd = getComponentsRequiredFields;

  requiredFieldsToAdd
    ?.filter((el) => !!el)
    ?.forEach(([name, id, value]) => {
      const existingField = requiredFields.find((field) => field.name === name);
      if (!!existingField) {
        existingField.value = value;
      } else {
        requiredFields.push(buildRequiredField(name, id, value));
      }
    });
};

const isFieldValid = (value, type) => {
  //check if email already exists
  if (type?.hasOwnProperty("otherEmails")) {
    const { otherEmails } = type;

    return value?.length > 0
      ? Array.isArray(value)
        ? !otherEmails?.find(
            (email) => value?.find((e) => e === email) === email
          )
        : !otherEmails?.find((email) => email === value)
      : false;
  }

  if (type === "name") {
    return value?.length > 2;
  }

  if (type?.hasOwnProperty("validity")) {
    return Array.isArray(value)
      ? value.every((e) => isValidEmail(e))
      : isValidEmail(value);
  }

  return true;
};

const willBuildValidityField = (el, fieldsValidity, setFieldsValidity) => {
  const [name, field, id, value, type] = el;

  const existingField = fieldsValidity.find(
    (field) => field.name === name && field.id === id
  );
  if (existingField) {
    existingField.value = value;
    existingField.isValid = isFieldValid(value, type);
    setFieldsValidity(
      fieldsValidity?.map((field: any) => {
        if (field.name === name && field.id === id) {
          return {
            ...existingField,
          };
        }
        return field;
      })
    );
  } else {
    if (id !== undefined)
      fieldsValidity.push(
        buildValidityField(name, field, id, value, isFieldValid(value, type))
      );
  }
};

export const logicForValidityFields = ({
  fieldsValidity,
  getComponentsValidityFields,
  setValidityFields = (a: Array<any>) => {},
}) => {
  const validityFieldsToAdd = getComponentsValidityFields;

  if (validityFieldsToAdd?.length === 0) setValidityFields([]);

  validityFieldsToAdd
    ?.filter((el) => !!el)
    ?.forEach((el) => {
      if (Array.isArray(el[0])) {
        el.forEach((el) => {
          willBuildValidityField(el, fieldsValidity, setValidityFields);
        });
      } else {
        willBuildValidityField(el, fieldsValidity, setValidityFields);
      }
    });
};

export const isYoutubeUrl = (url) => {
  const youtubeRegex =
    /^(?:(?:https?:)?\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  return url.match(youtubeRegex);
};

export const isVimeoUrl = (url) => url.toLowerCase().includes("vimeo.com");

export const isBrightcoveUrl = (url) =>
  url.toLowerCase().includes("brightcove");

export const isValidLinkedin = (url) => {
  const pattern =
    /^https?:\/\/(www\.)?linkedin\.com\/(in|pub|company|school)\/[a-zA-Z0-9_-]+\/?$/i;
  return pattern.test(url);
};

export const isBroadcomVideoUrl = (url) => {
  const lowerCaseUrl = url.toLowerCase();
  const isFromBroadcom = lowerCaseUrl.includes("learn.broadcom.com");
  const isVideoFile =
    lowerCaseUrl.endsWith(".mp4") ||
    lowerCaseUrl.endsWith(".mov") ||
    lowerCaseUrl.endsWith(".avi");

  return isFromBroadcom && isVideoFile;
};

export const isValidUrl = (urlString) => {
  const urlPattern = new RegExp(
    "^(https?:\\/\\/)?" + // validate protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // validate domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // validate OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // validate port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // validate query string
      "(\\#[-a-z\\d_]*)?$",
    "i"
  ); // validate fragment locator
  return !!urlPattern.test(urlString);
};

export const isValidEmail = (email) => {
  const regex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~’\-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;

  return regex.test(email);
};

export const buildErrorMessage = ({ component = {}, field }) => {
  //when using different components, pass it
  switch (component) {
    case "siteAdmin-partnerManager-validity":
      switch (field) {
        case "email-valid":
          return `Please enter a valid email to continue`;
        case "email-exists":
          return `The email that you provided is already in use`;
        case "name":
          return `The name must be longer than 2 characters`;
      }
    case "siteAdmin-partnerManager-required":
      switch (field) {
        case "name":
          return `Please enter a name to continue`;
        case "email":
          return `Please enter an email to continue`;
        case "partnermanagergroup":
          return "Please select at least one of the options from the Partner Manager Group";
      }
  }
};

export const saveFiltersOnBrowser = ({ tab, filters }) => {
  setLocalStorageItem(tab, JSON.stringify(filters));
};

export const FILTERS_PRESET_KEYS_PARTNERS = {
  partnerTypeAuthorizedEducationDelivery: "Authorized Education Delivery",
  searchPartnerName: "Partner Name",
  partnerTypeAuthorizedTechnicalSupport: "Authorized Technical Support",
  partnerTypeConsultingServices: "Authorized Consulting Services",
  partners_dashboard_products: "Products",
  partners_dashboard_services: "Services",
  partners_dashboard_locations: "Locations",
  partners_dashboard_languages: "Languages",
  partners_dashboard_useCasesType: "Customer Stories",
  partners_dashboard_certifications: "Certifications",
  partners_dashboard_verticals: "Verticals",
  partners_dashboard_regions: "Regions",
  partners_dashboard_divisions: "Business Unit",
  partners_dashboard_accreditations: "SASE Framework",
  partners_dashboard_hasKnights: "Show partners with Knights",
  partners_dashboard_isPublic: "Show Partners working in the Public Sector",
  partners_dashboard_isClearedResources: "Show Partners with cleared resources",
};

export const FILTERS_PRESET_KEYS_PROVIDER = {
  cloudModel: "Cloud Model",
  country: "Country",
  free_trial: "Free Trial",
  license_portability: "License Portability",
  providerServices: "Provider Services",
  verticalMarkets: "Vertical Markets",
  providerTypes: "Provider Types",
};

export const FILTERS_PRESET_KEYS_PROD_FORECAST_RENEWALS = {
  prodForecasts_oppHealth: "Renewal Health",
  prodForecasts_prodFamily: "Product Family",
  prodForecasts_prodSubFamily: "Product Subfamily",
  prodForecasts_quarter: "Quarter",
  prodForecasts_renewalStatus: "Status",
  per_page_prodForecasts: "View",
};

export const FILTERS_PRESET_KEYS_PROD_FORECAST_PIPELINES = {
  prodForecasts_hideDuplicates: "Hide Duplicates",
  prodForecasts_leadSource: "Lead Source",
  prodForecasts_onlyDuplicates: "Only Duplicates",
  prodForecasts_oppStage: "Opportunity Stage",
  prodForecasts_prodFamily: "Product Family",
  prodForecasts_prodSubFamily: "Product Subfamily",
  prodForecasts_quarter: "Quarter",
  prodForecasts_owner: "Opportunity Owner",
};

export const FILTERS_PRESET_KEYS_EVENTS_PROVIDER = {
  admin_csp: "Cloud Services",
  end_date_filter: "End Date",
  start_date_filter: "Start Date",
  regions: "Regions",
};

export const FILTERS_PRESET_KEYS_EVENTS_PARTNER = {
  admin_prod: "Products",
  end_date_filter: "End Date",
  start_date_filter: "Start Date",
  regions: "Regions",
};

export const FILTERS_PRESET_KEYS_MARKETPLACE = {
  p_category: "Category",
  p_formFactor: "Form Factor",
  p_price: "Price",
  p_visibility: "Visibility",
  per_page_p_marketplace: "View",
};

export const FILTERS_PRESET_KEYS_KNIGHTS = {
  knights_contentStatus: "Content Status",
  knights_locations: "Regions",
  knights_products: "Products",
  knights_type: "Types",
  per_page_knights: "View",
};

export const FILTERS_PRESET_KEYS_RESPONSE = {};

export const FILTERS_PRESET_KEYS_SERV_FORECAST = {
  forecasts_surveyStatus: "Survey Status",
  forecasts_statusServiceOpportunity: "Content Status",
  forecasts_quarter: "Quarter",
  forecasts_partnerOpportunityNumber: "Opportunity Number",
  per_page_forecasts: "View",
  forecasts_dealRegistrationNumber: "Broadcom Deal Registration Number",
};

export const FILTERS_PRESET_KEYS_CONNECTIONS = {
  connections_amount_max: "Connection Amount Max",
  connections_amount_min: "Connection Amount Min",
  connections_bu: "Business Unit",
  connections_country: "Country - Services Selected",
  connections_engScope: "Engagement Scope",
  connections_hasClearedResources: "Has Cleared Resources",
  connections_publicSector: "Public Sector",
  connections_quarter: "Quarter",
  connections_statusServiceOpportunity: "Changes Status",
  per_page_connections: "View",
};

export const getRolesArray = (role) => {
  if (role === "Partner/Provider") {
    return ["Partner - Admin", "Provider - Admin"];
  }

  return [role];
};

export enum AnalyticsCategory {
  RESOURCE = "RESOURCE",
}

export const cleanMarkdownBeforeDB = (text: string): string => {
  if (!text) return "";

  let cleaned = text;

  // First trim whitespace within paragraph tags
  cleaned = cleaned.replace(/<p>\s+|\s+<\/p>/g, (match) => {
    return match.startsWith("<p>") ? "<p>" : "</p>";
  });

  // Find the last paragraph that contains actual content
  const parts = cleaned.split(/<\/p>/);
  let lastContentIndex = parts.length - 1;

  while (lastContentIndex >= 0) {
    const part = parts[lastContentIndex];
    if (!part) {
      lastContentIndex--;
      continue;
    }

    // Check if this paragraph has any content beyond whitespace or <br> tags
    const content = part
      .replace(/<p>/, "")
      .replace(/<br\s*\/?>/g, "")
      .trim();
    if (content) break;
    lastContentIndex--;
  }

  // Keep only up to the last content paragraph plus its closing tag
  if (lastContentIndex >= 0) {
    cleaned = parts.slice(0, lastContentIndex + 1).join("</p>") + "</p>";
  }

  return cleaned;
};

export const areObjectEquals = <T>(object1: T, object2: T): boolean => {
  if (object1 === object2) {
    return true;
  }

  if (
    object1 === null ||
    object2 === null ||
    object1 === undefined ||
    object2 === undefined
  ) {
    return false;
  }

  if (typeof object1 !== typeof object2) {
    return false;
  }

  if (Array.isArray(object1) && Array.isArray(object2)) {
    if (object1.length !== object2.length) {
      return false;
    }
    return object1.every((item, index) =>
      areObjectEquals(item, object2[index])
    );
  }

  if (typeof object1 === "object" && typeof object2 === "object") {
    const keys1 = Object.keys(object1 as object);
    const keys2 = Object.keys(object2 as object);

    if (keys1.length !== keys2.length) {
      return false;
    }

    return keys1.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(object2, key) &&
        areObjectEquals(
          (object1 as Record<string, unknown>)[key],
          (object2 as Record<string, unknown>)[key]
        )
    );
  }

  // Handle primitive types
  return object1 === object2;
};
